import { Link } from 'react-router-dom';
import { SvgIcon } from '../../components/icons';
import { DsRoot } from '../ThemeProvider';
import { THEMES } from '../themes';
import { ThemePoster } from './ThemePoster';

/**
 * Landing page for the design exploration: the five directions with their
 * rationale, trade-offs and live miniatures, plus links into each full demo.
 */
export const DesignIndex = () => (
  <DsRoot theme="precision" mode="light" className="min-h-screen">
    <div style={{ background: 'var(--ds-bg)', minHeight: '100vh' }}>
      <div
        style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 20px 56px' }}
      >
        <header style={{ marginBottom: 28 }}>
          <div className="ds-eyebrow">Maintener · design exploration</div>
          <h1 className="ds-title" style={{ fontSize: 30, marginTop: 6 }}>
            Five directions for the UI overhaul
          </h1>
          <p className="ds-subtitle" style={{ maxWidth: 680, marginTop: 8 }}>
            Every direction is the same design system with a different token
            set — same components, same demo screen, same dummy data. Anything
            that looks different between them is the design direction, not
            different content. Each ships in light and dark, at two densities.
          </p>

          <div className="flex flex-wrap gap-2" style={{ marginTop: 14 }}>
            <Link to="/design/compare" className="ds-btn" data-ds-variant="secondary">
              <SvgIcon name="allInclusive" size={14} />
              Compare all five
            </Link>
            <Link
              to="/design/precision/gallery"
              className="ds-btn"
              data-ds-variant="ghost"
            >
              <SvgIcon name="palette" size={14} />
              Component gallery
            </Link>
            <Link to="/design/improvements" className="ds-btn" data-ds-variant="ghost">
              <SvgIcon name="addComment" size={14} />
              Improvement board
            </Link>
          </div>
        </header>

        <div className="flex flex-col gap-6">
          {THEMES.map((t, i) => (
            <section key={t.id} className="ds-card">
              <div className="flex flex-wrap" style={{ gap: 0 }}>
                {/* Copy */}
                <div
                  className="min-w-0"
                  style={{ flex: '1 1 340px', padding: 18 }}
                >
                  <div className="flex items-center gap-2">
                    <span className="ds-eyebrow">Direction {i + 1}</span>
                    {t.id === 'precision' && (
                      <span className="ds-chip" data-ds-tone="accent">
                        Recommended
                      </span>
                    )}
                  </div>

                  <h2
                    className="ds-title"
                    style={{ fontSize: 22, marginTop: 4 }}
                  >
                    {t.name}
                  </h2>
                  <div className="ds-muted" style={{ fontSize: 'var(--ds-fs-sm)' }}>
                    {t.mood}
                  </div>

                  <p style={{ marginTop: 10, fontSize: 'var(--ds-fs-sm)' }}>
                    {t.rationale}
                  </p>

                  <div className="flex flex-wrap gap-5" style={{ marginTop: 14 }}>
                    <div style={{ minWidth: 190 }}>
                      <div className="ds-eyebrow">Strengths</div>
                      <ul style={{ marginTop: 5 }}>
                        {t.pros.map((p) => (
                          <li
                            key={p}
                            className="flex items-start gap-1.5"
                            style={{ fontSize: 'var(--ds-fs-xs)', marginTop: 3 }}
                          >
                            <span style={{ color: 'var(--ds-ok-text)', marginTop: 1 }}>
                              <SvgIcon name="check" size={11} />
                            </span>
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div style={{ minWidth: 190 }}>
                      <div className="ds-eyebrow">Watch-outs</div>
                      <ul style={{ marginTop: 5 }}>
                        {t.cons.map((c) => (
                          <li
                            key={c}
                            className="flex items-start gap-1.5"
                            style={{ fontSize: 'var(--ds-fs-xs)', marginTop: 3 }}
                          >
                            <span style={{ color: 'var(--ds-warn-text)', marginTop: 1 }}>
                              <SvgIcon name="priorityHigh" size={11} />
                            </span>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 'var(--ds-r-md)',
                      background: 'var(--ds-surface-sunken)',
                    }}
                  >
                    <div className="ds-eyebrow">Accessibility</div>
                    <p
                      className="ds-muted"
                      style={{ fontSize: 'var(--ds-fs-xs)', marginTop: 3 }}
                    >
                      {t.a11y}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2" style={{ marginTop: 14 }}>
                    <Link
                      to={`/design/${t.id}`}
                      className="ds-btn"
                      data-ds-variant="primary"
                    >
                      Open demo
                      <SvgIcon name="keyboardArrowRight" size={14} />
                    </Link>
                    <Link
                      to={`/design/${t.id}/gallery`}
                      className="ds-btn"
                      data-ds-variant="ghost"
                    >
                      Components
                    </Link>
                  </div>
                </div>

                {/* Live miniature */}
                <div style={{ flex: '1 1 460px', padding: 18, minWidth: 0 }}>
                  <ThemePoster theme={t} />
                  <div
                    className="ds-muted"
                    style={{ fontSize: 'var(--ds-fs-xs)', marginTop: 6 }}
                  >
                    Shown in {t.defaultMode} mode · best for {t.bestFor}
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  </DsRoot>
);
